/**
 * One crown, one purse — and the cart ride that makes it cost something.
 *
 * > *"all resources from all cities and villages you own should be shared
 * > between, but make it so each city keeps its own stock, and if one city
 * > doesn't have enough wood and borrows from another, increase the building
 * > time"*
 *
 * Two things are being asked for at once and they pull in opposite directions,
 * which is what makes this worth a file rather than a helper. **Shared** means a
 * player who has spent an hour filling a village's barn should be able to spend
 * that grain in the capital, because it is their grain. **Its own stock** means
 * a town is still a place with a barn in it, so that where you built the sawmill
 * is a decision and a sacked town is a poorer town. A single pooled treasury
 * gives the first and destroys the second; four sealed treasuries give the
 * second and are the thing the owner is complaining about.
 *
 * So: **the stocks stay separate and the spending is pooled.** A town pays for
 * what it can out of its own store, and whatever it is short of is carted in
 * from the crown's other holdings — which is a real thing that really happens
 * and really takes time. The shortfall is recorded on the foundation as
 * `buildings.haul`, and the masons work slower for exactly as long as that
 * building is going up. A hall paid for entirely out of the town's own yards
 * goes up at full speed; one built on materials that had to come three
 * provinces down a road takes twice as long. Nothing is refused for being far
 * away, and nothing carted in is free.
 *
 * ## Where the purse lives, and why it is not hashed
 *
 * `World.purse` is a *shared object*, held by every settlement world the crown
 * runs, rebuilt by `linkCrown` at the top of every `stepGame`. It is topology,
 * not state: it says which worlds are in the same crown, the way a config says
 * which world this is. Every number it moves is moved out of a `res` bundle
 * that is already hashed and already saved, so a save round-trips exactly and
 * `hashWorld` needs to know nothing about it. Hash the purse and two players in
 * one room would disagree the moment one of them walked into a fourth town.
 *
 * **The order the members are visited is load-bearing.** A borrow walks the
 * members and takes from each in turn, so which town ends up short depends on
 * that order — and the order a `Map` iterates is the order the player happened
 * to enter their towns. `linkCrown` sorts by province index, which is a
 * property of the board rather than of the session, so a replay borrows from
 * the same barn the original run did.
 *
 * ## What a village is
 *
 * > *"villages, not cities, should have their own set of buildings separate to
 * > the city — they should be bonus material for the city, not full cities
 * > themselves"*
 *
 * Campaign villages now have permanent identity and a fixed specialization,
 * carried into their local world by `config.villageType`. They upgrade from
 * level one to seven through the campaign command; no number of buildings or
 * levels turns them into cities. Their presets supply the visual settlement
 * and battle ground, while the campaign pays their passive production into an
 * owned city's warehouse. Blocked shipments remain saved at the village.
 *
 * A visited village does not run a second local economy or join this shared
 * purse. Cities remain manually managed worlds with their own stores. The
 * rung-based helpers below survive only for untagged legacy worlds; current
 * village presets are explicitly excluded from local promotion.
 */

import { RESOURCE_KEYS, type ResourceBundle } from '../types.ts';
import { LogTone, pushLog } from '../events.ts';
import type { World } from '../world.ts';

/**
 * The rung at which a holding stops being a village.
 *
 * 4, which is the number `travel.ts` has always printed "a village" below.
 * Taking the existing vocabulary rather than inventing a second one matters
 * more than the number does: a player told on the world map that Thistlemoor is
 * a village must find a village when they walk into it.
 */
export const VILLAGE_RUNG = 4;

/** What the realm calls a holding of this rung. The seat is asked for by name. */
export function settlementKind(tier: number): 'village' | 'town' | 'city' {
  if (tier >= 6) return 'city';
  if (tier >= VILLAGE_RUNG) return 'town';
  return 'village';
}

/**
 * Is this settlement world a village?
 *
 * `w.rung` is 0 on every world that is not a holding of a realm — the capital,
 * a skirmish, a standalone siege, every world made before this field existed —
 * and those are all full settlements. So the default answer is no, and every
 * existing run, save and replay is untouched.
 */
export function isVillage(w: World): boolean {
  if (w.config.villageType !== undefined) return !!w.config.villageType;
  return w.rung > 0 && w.rung < VILLAGE_RUNG;
}

/**
 * How much harder a village's producers work.
 *
 * A village has a third of a city's catalogue and none of its institutions, so
 * without this the answer to "should I build up the hamlet I just took" is
 * always no. A rung-2 holding runs about nine producers against a seat's
 * thirty, so a third again on nine is worth roughly a tenth of the seat's
 * output — a contribution, not a second capital, which is the balance being
 * asked for.
 */
export const VILLAGE_YIELD = 1.35;

/** The multiplier on a settlement's raw production. 1 for anything but a village. */
export function yieldMul(w: World): number {
  return isVillage(w) ? VILLAGE_YIELD : 1;
}

/**
 * The crown's holdings, as one purse.
 *
 * Deliberately a mutable object with a member list rather than a bundle of
 * numbers: the numbers *are* the members' own `res`, and a copy would have to
 * be written back, which is a second source of truth for the one thing in this
 * game a player watches most closely.
 */
export interface CrownPurse {
  /** Every settlement world of the crown's, in province order. */
  members: World[];
}

const EMPTY: readonly World[] = [];

/**
 * Put every town of the crown's into one purse, in a stable order.
 *
 * Called from `stepGame` before anything is stepped, so a command applied this
 * tick sees the roster as it is this tick. Cheap by construction — it is a sort
 * of at most a handful of worlds — and it rebuilds rather than patches, because
 * a town can be lost as well as founded and a stale member is a barn that pays
 * for things after it has been burnt.
 */
export function linkCrown(members: readonly (World | null | undefined)[]): void {
  const live: World[] = [];
  for (const w of members) {
    if (!w || live.includes(w)) continue;
    // The realm is not a settlement and has no store worth the name.
    if (w.campaign) continue;
    if (w.config.villageType) continue;
    live.push(w);
  }
  live.sort((a, b) => (a.config.province ?? -1) - (b.config.province ?? -1));
  const purse: CrownPurse = { members: live };
  for (const w of live) w.purse = purse;
}

/** Every member of this world's crown other than itself, in order. */
function siblings(w: World): readonly World[] {
  const all = w.purse?.members;
  if (!all || all.length < 2) return EMPTY;
  return all.filter((o) => o !== w);
}

/**
 * What the crown holds, everywhere, added up.
 *
 * For the UI, which wants a number to print, and for nothing in the simulation
 * — the simulation never adds the stores together, it walks them. Takes a plain
 * list so a caller with a `Map` of towns is not obliged to have a purse yet.
 */
export function crownPool(members: readonly World[]): ResourceBundle {
  const out = {} as ResourceBundle;
  for (const k of RESOURCE_KEYS) out[k] = 0;
  for (const w of members) {
    if (w.config.villageType) continue;
    for (const k of RESOURCE_KEYS) out[k] += w.res[k];
  }
  return out;
}

/**
 * Can this town pay, counting what its neighbours could send?
 *
 * The drop-in for `canAfford(w.res, cost)` at every call site in `apply.ts`. A
 * world with no purse — a skirmish, a standalone siege, a crown with one town —
 * answers exactly what `canAfford` answered, which is what makes this safe to
 * put in front of twenty existing refusals.
 */
export function crownAfford(w: World, cost: Partial<ResourceBundle>): boolean {
  const others = siblings(w);
  for (const k of RESOURCE_KEYS) {
    const need = cost[k] ?? 0;
    if (need <= 0) continue;
    let have = w.res[k];
    for (const o of others) have += o.res[k];
    if (have < need) return false;
  }
  return true;
}

/**
 * Pay, from this town's store first and the crown's other barns after.
 *
 * Returns **the share of the bill that had to be carted in**, by value: 0 when
 * the town paid for all of it out of its own yards, 1 when every last plank came
 * from somewhere else. `doBuild` writes that number onto the foundation and the
 * masons are slowed by it; everything else that spends — a company, a wall, a
 * tech node — ignores it, because those are instants and there is no duration
 * for the cart ride to lengthen.
 *
 * "By value" is the weighted share across every resource in the bill, weighted
 * by the amount of each, so a hall that was ten planks short of two hundred is
 * barely slowed and one built entirely on imports is slowed all the way. A
 * count of *which resources* were short would say the same thing about both.
 *
 * The caller is expected to have asked `crownAfford` first. If it did not, this
 * takes everything there is and reports what it could not find as carted —
 * which keeps a mis-ordered call site from silently minting materials.
 */
export function crownSpend(w: World, cost: Partial<ResourceBundle>): number {
  const others = siblings(w);
  let billed = 0;
  let carted = 0;
  for (const k of RESOURCE_KEYS) {
    const need = cost[k] ?? 0;
    if (need === 0) continue;
    // A refund is a negative cost and goes back where it was spent from, which
    // is here: the town that demolished the thing is the town holding the beams.
    if (need < 0) {
      w.res[k] -= need;
      continue;
    }
    billed += need;
    const own = Math.min(w.res[k], need);
    w.res[k] -= own;
    let short = need - own;
    for (const o of others) {
      if (short <= 0) break;
      const take = Math.min(o.res[k], short);
      if (take <= 0) continue;
      o.res[k] -= take;
      short -= take;
    }
    carted += need - own;
  }
  return billed > 0 ? Math.min(1, carted / billed) : 0;
}

/** Historical building-count threshold for untagged legacy worlds only. */
export const PROMOTE_STANDING = 28;

/**
 * Preserve historical daily progression for worlds that still lack an explicit
 * identity tag. Every current city and village preset has a tag and leaves
 * immediately: campaign villages only change level through paid realm works,
 * and their identity stays village at every level.
 */
export function promoteVillages(members: readonly World[]): void {
  for (const w of members) {
    if (w.config.villageType !== undefined) continue;
    if (!isVillage(w)) continue;
    const b = w.buildings;
    let standing = 0;
    for (let i = 0; i < b.slots.high; i++) {
      if (b.slots.alive[i] === 0 || b.build[i] < 1) continue;
      standing += 1 + (b.tier[i] - 1) * 0.5;
    }
    if (standing < PROMOTE_STANDING * w.rung) continue;
    w.rung = Math.min(VILLAGE_RUNG, w.rung + 1);
    if (!isVillage(w)) {
      pushLog(
        w.events,
        'It is a town now. The masons will take work they would not have looked at last year.',
        LogTone.Good,
      );
    }
  }
}
